import {
  Accidental,
  Modifier,
  RuntimeError,
  Vex,
  StaveNote as VexStaveNote,
} from "vexflow";
import {
  DEFAULT_NOTE_STYLE,
  HIGHLIGHT_NOTE_STYLE,
} from "../constants";
import { NoteMap } from "../noteMap";
import { clefBassTransform, mergeProperties } from "../../utils/helpers";
import { MidiStruct, PlayInfo } from "../player";

const MIDI: MidiStruct = window.MIDI as unknown as MidiStruct;

export class StaveNote extends VexStaveNote {
  public indexOfKey = (keyName: string) => {
    return this.keys.indexOf(keyName);
  };

  clone = (newProps: any) => {
    const currentProps = {
      keys: this.keys,
      stem_direction: this.getStemDirection(),
      duration: this.duration,
      noteType: this.noteType,
      clef: this.clef,
    };

    var mergedProps = mergeProperties(currentProps, newProps);
    mergedProps.duration = mergedProps.duration + mergedProps.noteType;

    var newNote = new StaveNote(mergedProps);

    //Setting the style as the same style as the note head
    const style = this.getStyle();
    if (style) newNote.setStyle(style);

    // if (this.modifierContext != null && this.getDots() != null)
    //   newNote.addDotToAll();

    const beam = this.getBeam();
    if (beam) newNote.setBeam(beam);

    //Clone modifiers
    const modifiers = this.getModifiers();
    modifiers.forEach((modifier: Modifier) => {
      newNote.addModifier(modifier, modifier.getIndex());
    });

    return newNote;
  };

  setHighlight = (highlight: boolean) => {
    if (highlight) this.setStyle(HIGHLIGHT_NOTE_STYLE);
    else this.setStyle(DEFAULT_NOTE_STYLE);
  };

  /**
   * Return new note, from actions:
   * "UP"		=> up on stave
   * "DOWN"	=> down on stave
   * "SHARP"	=> add sharp
   * @param action
   * @returns
   */
  public change = (action: string) => {
    var newKeys = new Array();
    //for each key
    for (var k in this.keys) {
      var splitted = this.keys[k].split("/");
      splitted[0] = splitted[0].toUpperCase();
      //first char is the note
      var note = splitted[0].substring(0, 1);
      var accidentals = splitted[0].substring(1);
      var octave = parseInt(splitted[1]);

      //search index of key in NoteMap.noteMap
      let index: number = 0;
      for (let x = 0; x < NoteMap.noteMap.length; x++) {
        if (NoteMap.noteMap[x] == note) {
          index = x;
          break;
        }
      }

      //do the ACTION
      if (action == "UP")
        //increment key
        index++;
      else if (action == "DOWN")
        //descrease key
        index--;
      else if (action == "SHARP") {
        //TODO add other cases
        switch (accidentals) {
          case "":
          case null:
          case undefined:
            accidentals = "#";
            break;
          case "#":
            accidentals = "##";
            break;
        }
        alert(accidentals);
      }
      //octave changes
      if (index == NoteMap.noteMap.length) {
        index = 0;
        octave++;
      } else if (index < 0) {
        index = NoteMap.noteMap.length - 1;
        octave--;
      }
      //push new key
      note = NoteMap.noteMap[index];
      newKeys.push(note + accidentals + "/" + octave);
    }
    let dur = this.getDuration();
    let type = this.getNoteType();

    if (action == "PAUSE") {
      // if (type == "n") type = "r";
      // else if (type == "r") type = "n";
      // dur = Vex.Flow.parseNoteDurationString(this.duration);
      // var dots = "";
      // for (var i = 0; i < dur.dots; i++) dots += "d";
      // dur = dur.duration + dots + type;
    }
    //create new note with new parameters
    return this.clone({ keys: newKeys, duration: dur });
  };

  /*
   * Override the original draw flag to fix the color being drawed
   */
  drawFlag = () => {
    const ctx = this.getContext();
    if (!ctx)
      throw new RuntimeError(
        "NoCanvasContext",
        "Can't draw without a canvas context."
      );
    const glyph = this.getGlyph();
    const render_flag = this.beam === null;
    const bounds = this.getNoteHeadBounds();

    const x_begin = this.getNoteHeadBeginX();
    const x_end = this.getNoteHeadEndX();

    if (glyph.flag && render_flag) {
      const stem = this.getStem();
      let note_stem_height = 0;
      if (stem) note_stem_height = stem.getHeight();
      var flag_x, flag_y, flag_code;

      if (this.getStemDirection() === Vex.Flow.Stem.DOWN) {
        // Down stems have flags on the left.
        flag_x = x_begin + 1;
        flag_y = bounds.y_top - note_stem_height + 2;
        flag_code = glyph.code_flag_downstem;
      } else {
        // Up stems have flags on the left.
        flag_x = x_end + 1;
        flag_y = bounds.y_bottom - note_stem_height - 2;
        flag_code = glyph.code_flag_upstem;
      }

      //NOTE: The 2 lines below were added to the original code
      var ctxFillStyle = ctx.fillStyle;
      const style = this.getStyle();
      if (typeof style?.fillStyle == "string") {
        ctx.fillStyle = style.fillStyle;
      }

      // Draw the Flag
      //   Vex.Flow.renderGlyph(
      //     ctx,
      //     flag_x,
      //     flag_y,
      //     this.render_options.glyph_font_scale,
      //     flag_code
      //   );

      //NOTE: The 2 lines below were added to the original code
      //Reverting style
      ctx.fillStyle = ctxFillStyle;
    }
  };

  getPlayEvents = (playInfo: PlayInfo) => {
    //Prepare the notes to be sent
    let notes: any[] = [];

    this.keys.map((key: string) => {
      const finalKey = playInfo.clef === 'bass' ? clefBassTransform(key) : key;
      notes.push(MIDI.keyToNote[finalKey.replace("/", "")]);
    });

    let keyPressTime = playInfo.defaultTime / parseInt(this.duration);

    this.getModifiers().map((modifier: any) => {
      let modValue: number = 0;
      if (modifier instanceof Accidental) {
        switch (modifier.type) {
          case "bb":
            modValue = -2;
            break;
          case "b":
            modValue = -1;
            break;
          case "n":
            modValue = 0;
            break;
          case "#":
            modValue = 1;
            break;
          case "##":
            modValue = 2;
            break;
        }

        notes = notes.map((note: number) => {
          return (note += modValue);
        });
      } else if (modifier instanceof Vex.Flow.Dot) {
        keyPressTime *= 1.5;
      }
    });

    var events = [];

    events.push({
      type: "channel",
      channel: 0,
      subtype: notes.length == 1 ? "noteOn" : "chordOn",
      noteNumber: notes.length == 1 ? notes[0] : notes,
      velocity: 127,
      queuedTime: playInfo.delay,
      note: this,
    });
    events.push({
      type: "channel",
      channel: 0,
      subtype: notes.length == 1 ? "noteOff" : "chordOff",
      noteNumber: notes.length == 1 ? notes[0] : notes,
      queuedTime: playInfo.delay + keyPressTime,
      note: this,
    });

    //increment the delay
    playInfo.delay = playInfo.delay + keyPressTime;

    return events;
  };
}
